import {
	Box,
	createStyles,
	Group,
	Input,
	Loader,
	Modal,
	Stack,
	TextInput,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	fetchApiCreateAnnouncement,
	fetchApiUpdateAnnouncement,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Button } from '@repo/foundations';
import { compact, debounce, map, uniq } from 'lodash-es';
import { Suspense, useCallback, useState } from 'react';
import type { ISecodaEntity } from '../../api';
import { useAuthUser } from '../../api';
import type { SecodaEntity } from '../../lib/models';

import { invalidateNotificationListQuery } from '../../api/hooks/notification/utils';
import type { BreadcrumbEntity } from '../../utils/breadcrumb';
import { getBreadcrumbForEntity } from '../../utils/breadcrumb';
import { ErrorBoundary } from '../ErrorBoundary';
import { RichEditor } from '../RichEditor';
import { AnnouncementEntityRecipients } from './AnnouncementEntityRecipients';
import type { AnnouncementRelatedResourceItem } from './AnnouncementRelatedResource';
import { AnnouncementRelatedResource } from './AnnouncementRelatedResource';
import type { AnnouncementFormKeys, IAnnouncementFieldProps } from './types';
import { AnnouncementDefaultFormData } from './types';

const useStyles = createStyles((theme, error: string | null | undefined) => ({
	editorWrapper: {
		padding: theme.spacing.sm,
		borderRadius: theme.radius.xs,
		border: `1px solid ${error ? theme.other.getColor('fill/critical/default') : theme.other.getColor('border/primary/default')}`,

		'.heading-actions, .block-menu-trigger': {
			display: 'none !important',
		},
		'.heading-content::before': {
			display: 'none !important',
		},
		'h1::before, h2::before, h3::before, h4::before, h5::before, h6::before': {
			display: 'none !important',
		},
	},
}));

function BodyInput({
	onChange,
	error,
	initialValue,
}: IAnnouncementFieldProps & { initialValue?: string }) {
	const { classes } = useStyles(error);

	const handleEditorChange = useCallback(
		(value?: string) => {
			onChange('definition')(value ?? '');
		},
		[onChange]
	);

	return (
		<Input.Wrapper label="Body" error={error}>
			<Box className={classes.editorWrapper}>
				<RichEditor
					onChangeCallback={handleEditorChange}
					readOnly={false}
					disableTopGap
					initialValue={initialValue}
					disableExtensions={['comment']}
					emptyDocPlaceholder="Write announcement..."
				/>
			</Box>
		</Input.Wrapper>
	);
}

export interface IAnnouncement {
	notification_id: string;
	announcement_id: string;
	subject: string;
	definition: string;
	recipients: string[];
	receiver_slack_channel_ids: string[];
	related_resources: ISecodaEntity[];
}

function calculateFormData(
	initialData: IAnnouncement | undefined,
	entity?: SecodaEntity | ISecodaEntity
): {
	subject: string;
	definition: string;
	recipients: string[];
	receiver_slack_channel_ids: string[];
	related_resources: string[];
} {
	if (initialData) {
		return {
			subject: initialData.subject,
			definition: initialData.definition,
			recipients: initialData.recipients,
			receiver_slack_channel_ids: initialData.receiver_slack_channel_ids,
			related_resources: initialData.related_resources.map(
				(resource) => resource.id
			),
		};
	}

	// If no initial data but entity is provided, include entity ID in related resources
	if (entity) {
		return {
			...AnnouncementDefaultFormData,
			related_resources: [entity.id],
		};
	}

	return AnnouncementDefaultFormData;
}

function createInitialItem(
	entity: SecodaEntity | ISecodaEntity | undefined,
	initialData: IAnnouncement | undefined
): AnnouncementRelatedResourceItem[] {
	if (entity) {
		const breadcrumbs = getBreadcrumbForEntity({
			entity: entity as BreadcrumbEntity,
			nativeType: entity.native_type || '',
			team: null,
		});

		return [
			{
				value: entity.id,
				label: entity.title || 'Untitled',
				entity: entity as ISecodaEntity,
				breadcrumbText: compact(map(breadcrumbs, 'title')).join(' / '),
			},
		];
	}

	if (initialData) {
		return initialData.related_resources.map((resource) => ({
			value: resource.id,
			label: resource.title || 'Untitled',
			entity: resource,
			breadcrumbText: 'temp text for now',
		}));
	}

	return [];
}

function calculateSelectedIds(
	entity: SecodaEntity | ISecodaEntity | undefined,
	initialData:
		| {
				related_resources: ISecodaEntity[];
		  }
		| undefined
): string[] {
	if (entity) {
		return [entity.id];
	}

	if (initialData) {
		return initialData.related_resources.map((resource) => resource.id);
	}

	return [];
}

type IAnnouncementModalProps = {
	entity?: SecodaEntity | ISecodaEntity;
	opened: boolean;
	close: () => void;
	mode?: 'create' | 'edit';
	initialData?: IAnnouncement;
};

function AnnouncementModal({
	entity,
	opened,
	close,
	mode = 'create',
	initialData,
}: IAnnouncementModalProps) {
	const { user, workspace } = useAuthUser();
	const [formData, setFormData] = useState(
		calculateFormData(initialData, entity)
	);
	const [selectedIds, setSelectedIds] = useState<string[]>(
		calculateSelectedIds(entity, initialData)
	);
	const [selectedData, setSelectedData] = useState<
		AnnouncementRelatedResourceItem[]
	>(createInitialItem(entity, initialData));
	const [subjectError, setSubjectError] = useState<string | null>(null);
	const [bodyError, setBodyError] = useState<string | null>(null);

	const debounceChange = debounce(
		(key: AnnouncementFormKeys, value: string | string[]) => {
			setFormData((prev) => ({ ...prev, [key]: value }));
		},
		300
	);

	const handleChange = useCallback(
		(key: AnnouncementFormKeys) => (value: string | string[]) => {
			if (formData[key] === value) return;

			// Only debounce certain properties that can't be edited simultaneously.
			// don't debounce definition - the Editor already debounces it
			if (['subject'].includes(key)) {
				debounceChange(key, value);
			} else {
				setFormData((prev) => ({ ...prev, [key]: value }));
			}
		},
		[debounceChange, formData]
	);

	const handleSubmit = useCallback(async () => {
		const values = formData;
		let hasError = false;

		if (!formData.subject.trim()) {
			setSubjectError('Subject is required');
			hasError = true;
		}

		if (!values.definition.trim()) {
			setBodyError('Body is required');
			hasError = true;
		}

		if (hasError) {
			return;
		}

		try {
			if (mode === 'edit') {
				if (!initialData?.notification_id) {
					showNotification({
						title: 'Error',
						message: 'Notification ID is required for editing',
						color: 'red',
					});
					return;
				}

				await fetchApiUpdateAnnouncement({
					body: {
						notification_id: initialData.notification_id,
						announcement_id: initialData.announcement_id ?? null,
						title: values.subject,
						definition: values.definition,
						receiver_slack_channel_ids: uniq(values.receiver_slack_channel_ids),
						related_resources: uniq(values.related_resources),
						receivers: uniq(values.recipients),
					},
				});

				invalidateNotificationListQuery();

				showNotification({
					message: 'Announcement Updated',
					color: 'green',
				});

				close();
				return;
			}

			await fetchApiCreateAnnouncement({
				body: {
					title: values.subject,
					definition: values.definition,
					receivers: values.recipients,
					receiver_slack_channel_ids: uniq(values.receiver_slack_channel_ids),
					related_resources: uniq(values.related_resources),
				},
			});

			showNotification({
				message: 'Announcement Sent',
				color: 'green',
			});
			trackEvent('inbox/send_announcement/click', {}, user, workspace);
			invalidateNotificationListQuery();
			close();
		} catch (error) {
			showNotification({
				title:
					mode === 'edit'
						? 'Error updating announcement'
						: 'Error sending announcement',
				message:
					error instanceof Error
						? error.message
						: 'An unexpected error occurred',
				color: 'red',
			});
			trackEvent(
				'inbox/send_announcement/error',
				{ error: String(error) },
				user,
				workspace
			);
		}
	}, [formData, close, user, workspace, mode, initialData]);

	return (
		<Modal
			size="lg"
			opened={opened}
			onClose={close}
			title={mode === 'edit' ? 'Edit announcement' : 'Create announcement'}
		>
			<Stack spacing="md">
				<TextInput
					data-autofocus
					label="Subject"
					error={subjectError}
					defaultValue={formData.subject}
					onChange={(event) => {
						handleChange('subject')(event.target.value ?? '');
						setSubjectError(null);
					}}
				/>
				<ErrorBoundary>
					<Suspense fallback={<Loader />}>
						<AnnouncementEntityRecipients
							onChange={handleChange}
							selectedRecipients={initialData?.recipients}
							selectedReceiverSlackChannelIds={
								initialData?.receiver_slack_channel_ids
							}
							selectedData={selectedData}
						/>
					</Suspense>
				</ErrorBoundary>
				<AnnouncementRelatedResource
					onChange={handleChange}
					value={selectedIds}
					setValue={setSelectedIds}
					selectedData={selectedData}
					setSelectedData={setSelectedData}
				/>
				<BodyInput
					error={bodyError}
					initialValue={formData.definition}
					onChange={(key) => (value) => {
						handleChange(key)(value);
						setBodyError(null);
					}}
				/>
			</Stack>
			<Group position="right" mt={12}>
				<Button onClick={close}>Cancel</Button>
				<Button variant="primary" onClick={handleSubmit}>
					{mode === 'edit' ? 'Save announcement' : 'Send announcement'}
				</Button>
			</Group>
		</Modal>
	);
}

export default AnnouncementModal;
